<template>
    <div class="dqd-wrapper">
        <div v-if="loading" class="dqd-content">

            <!-- 헤더 스켈레톤 -->
            <div class="dqd-header">
                <div class="dqd-skeleton dqd-skeleton-date"></div>
                <div class="dqd-skeleton dqd-skeleton-status"></div>
            </div>

            <!-- Overall Metrics 스켈레톤 -->
            <div class="dqd-overall-metrics">
                <div class="dqd-metric-item">
                    <div class="dqd-skeleton dqd-skeleton-label"></div>
                    <div class="dqd-skeleton dqd-skeleton-value"></div>
                </div>
                <div class="dqd-metric-item">
                    <div class="dqd-skeleton dqd-skeleton-label"></div>
                    <div class="dqd-skeleton dqd-skeleton-value"></div>
                </div>
                <div class="dqd-metric-item">
                    <div class="dqd-skeleton dqd-skeleton-label"></div>
                    <div class="dqd-skeleton dqd-skeleton-value"></div>
                </div>
            </div>

            <!-- 카드 섹션 스켈레톤 -->
            <div class="dqd-cards-section">
                <div class="dqd-section-header">
                    <div class="dqd-skeleton dqd-skeleton-title"></div>
                    <div class="dqd-skeleton dqd-skeleton-count"></div>
                </div>
                <div class="dqd-cards-container">
                    <div v-for="n in 2" :key="n" class="dqd-data-card">
                        <div class="dqd-card-header">
                            <div class="dqd-card-title-group">
                                <div class="dqd-skeleton dqd-skeleton-facility"></div>
                                <div class="dqd-skeleton dqd-skeleton-addr"></div>
                            </div>
                            <div class="dqd-skeleton dqd-skeleton-badge"></div>
                        </div>
                        <div class="dqd-card-metrics">
                            <div class="dqd-metric-item" v-for="m in 6" :key="m">
                                <div class="dqd-skeleton dqd-skeleton-metric-name"></div>
                                <div class="dqd-skeleton dqd-skeleton-metric-number"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-else-if="detailData" class="dqd-content">
            <div class="dqd-header">
                <div class="dqd-date-display">{{ formatDate(selectedDate) }}</div>
                <div class="dqd-overall-status" :class="`dqd-status-${detailData.overall?.level?.toLowerCase()}`">
                    <span class="dqd-status-label">{{ getLevelLabel(detailData.overall?.level) }}</span>
                </div>
            </div>

            <div class="dqd-overall-metrics">
                <div class="dqd-metric-item">
                    <span class="dqd-metric-label">{{ getColumnLabel('rawDataRate') }}</span>
                    <span class="dqd-metric-value">{{ detailData.overall?.rawDataRate?.toFixed(1) }}%</span>
                </div>
                <div class="dqd-metric-item">
                    <span class="dqd-metric-label">{{ getColumnLabel('summary1minRate') }}</span>
                    <span class="dqd-metric-value">{{ detailData.overall?.summary1minRate?.toFixed(1) }}%</span>
                </div>
                <div class="dqd-metric-item">
                    <span class="dqd-metric-label">{{ getColumnLabel('summary15minRate') }}</span>
                    <span class="dqd-metric-value">{{ detailData.overall?.summary15minRate?.toFixed(1) }}%</span>
                </div>
            </div>

            <div class="dqd-cards-section">
                <div class="dqd-section-header">
                    <div class="dqd-section-title">측정점별 상세 정보</div>
                    <div class="dqd-section-count">총 {{ detailData.rows?.length || 0 }}개</div>
                </div>
                <div class="dqd-cards-container">
                    <div v-for="(row, index) in detailData.rows" :key="index" class="dqd-data-card"
                        :class="`dqd-card-${row.values?.qualityLevel?.toLowerCase()}`">
                        <div class="dqd-card-header">
                            <div class="dqd-card-title-group">
                                <div class="dqd-card-facility">{{ row.facilityName }}</div>
                                <div class="dqd-card-addr">{{ row.ptAddr }}</div>
                            </div>
                            <div class="dqd-card-level">
                                <span class="dqd-level-badge"
                                    :class="`dqd-badge-${row.values?.qualityLevel?.toLowerCase()}`">
                                    {{ getLevelLabel(row.values?.qualityLevel) }}
                                </span>
                            </div>
                        </div>
                        <div class="dqd-card-metrics">
                            <div class="dqd-metric-item">
                                <span class="dqd-metric-name">{{ getColumnLabel('rawDataRate') }}</span>
                                <span class="dqd-metric-number">{{ row.values?.rawDataRate?.toFixed(1) }}%</span>
                            </div>
                            <div class="dqd-metric-item">
                                <span class="dqd-metric-name">{{ getColumnLabel('summary1minRate') }}</span>
                                <span class="dqd-metric-number">{{ row.values?.summary1minRate?.toFixed(1) }}%</span>
                            </div>
                            <div class="dqd-metric-item">
                                <span class="dqd-metric-name">{{ getColumnLabel('summary15minRate') }}</span>
                                <span class="dqd-metric-number">{{ row.values?.summary15minRate?.toFixed(1) }}%</span>
                            </div>
                            <div class="dqd-metric-divider"></div>
                            <div class="dqd-metric-item">
                                <span class="dqd-metric-name">{{ getColumnLabel('rawCount') }}</span>
                                <span class="dqd-metric-number">{{ row.values?.rawCount || 0 }}</span>
                            </div>
                            <div class="dqd-metric-item" :class="getMissingRatioClass(row.values?.missingRatio)">
                                <span class="dqd-metric-name">{{ getColumnLabel('missingRatio') }}</span>
                                <span class="dqd-metric-number">{{ row.values?.missingRatio?.toFixed(2) }}%</span>
                            </div>
                            <div class="dqd-metric-item" :class="getAnomalyClass(row.values?.anomalyCount)">
                                <span class="dqd-metric-name">{{ getColumnLabel('anomalyCount') }}</span>
                                <span class="dqd-metric-number">{{ row.values?.anomalyCount || 0 }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-else class="dqd-empty-state">
            <div class="dqd-empty-text">날짜를 선택하여 상세 정보를 확인하세요</div>
        </div>
    </div>
</template>

<script>

const QUALITY_LEVEL_LABELS = {
    'ERROR': '오류',
    'CRITICAL': '심각',
    'WARNING': '경고',
    'NORMAL': '정상',
    'UNKNOWN': '알 수 없음'
}
export default {
    props: {
        selectedDate: {
            type: String,
            default: null
        },
        detailData: {
            type: Object,
            default: null
        },
        loading: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {};
    },
    methods: {
        formatDate(dateStr) {
            if (!dateStr) return '';
            const year = dateStr.substring(0, 4);
            const month = dateStr.substring(4, 6);
            const day = dateStr.substring(6, 8);
            return `${year}년 ${month}월 ${day}일`;
        },
        getColumnLabel(key) {
            if (!this.detailData?.metrics?.columns) return key;
            const column = this.detailData.metrics.columns.find(col => col.key === key);
            return column?.label || key;
        },
        getLevelLabel(level) {
            if (!level) return '-';
            return QUALITY_LEVEL_LABELS[level] || level;
        },
        getMissingRatioClass(ratio) {
            if (ratio >= 10) return 'dqd-ratio-high';
            if (ratio >= 5) return 'dqd-ratio-medium';
            return 'dqd-ratio-low';
        },
        getAnomalyClass(count) {
            if (count > 0) return 'dqd-anomaly-exists';
            return '';
        }
    }
};
</script>

<style scoped>
.dqd-wrapper {
    height: 100%;
    overflow: auto;
    padding: 16px;
}

.dqd-empty-state {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #6b7280;
    font-size: 14px;
}

.dqd-content {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.dqd-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    /* padding-bottom: 12px; */
    /* border-bottom: 2px solid #e5e7eb; */
}

.dqd-date-display {
    font-size: 16px;
    font-weight: 700;
    color: #111827;
}

.dqd-overall-status {
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 700;
}

.dqd-status-error {
    background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
    color: #991b1b;
}

.dqd-status-critical {
    background: linear-gradient(135deg, #fed7aa 0%, #fdba74 100%);
    color: #9a3412;
}

.dqd-status-warning {
    background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
    color: #92400e;
}

.dqd-status-normal {
    background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
    color: #065f46;
}

.dqd-status-unknown {
    background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
    color: #374151;
}

.dqd-overall-metrics {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.dqd-overall-metrics .dqd-metric-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    font-size: 12px;
}

.dqd-overall-metrics .dqd-metric-label {
    color: #64748b;
    font-weight: 500;
    flex: 1;
    text-align: left;
}

.dqd-overall-metrics .dqd-metric-value {
    font-weight: 700;
    color: #0f172a;
    flex-shrink: 0;
    text-align: right;
    min-width: 50px;
}

.dqd-cards-section {
    margin-top: 4px;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

.dqd-section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.dqd-section-title {
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
}

.dqd-section-count {
    font-size: 11px;
    color: #64748b;
    font-weight: 500;
}

.dqd-cards-container {
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-height: 100%;
    overflow-y: auto;
    flex: 1;
    min-height: 0;
}

.dqd-data-card {
    background: transparent;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 8px 10px;
    transition: all 0.2s;
}

.dqd-data-card:hover {
    border-color: #cbd5e1;
}

.dqd-card-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 10px;
}

.dqd-card-title-group {
    flex: 1;
    min-width: 0;
}

.dqd-card-facility {
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 4px;
    line-height: 1.4;
}

.dqd-card-addr {
    font-size: 10px;
    color: #94a3b8;
    line-height: 1.4;
}

.dqd-card-level {
    flex-shrink: 0;
    margin-left: 8px;
}

.dqd-level-badge {
    display: inline-block;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 9px;
    font-weight: 700;
}

.dqd-badge-error {
    background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
    color: #991b1b;
}

.dqd-badge-critical {
    background: linear-gradient(135deg, #fed7aa 0%, #fdba74 100%);
    color: #9a3412;
}

.dqd-badge-warning {
    background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
    color: #92400e;
}

.dqd-badge-normal {
    background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
    color: #065f46;
}

.dqd-badge-unknown {
    background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
    color: #374151;
}

.dqd-card-metrics {
    display: flex;
    flex-direction: column;
    gap: 3px;
}

.dqd-metric-divider {
    height: 1px;
    background: #e2e8f0;
    margin: 4px 0;
}

.dqd-metric-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
}

.dqd-metric-name {
    font-size: 12px;
    color: #64748b;
    font-weight: 500;
    flex: 1;
    text-align: left;
}

.dqd-metric-number {
    font-size: 12px;
    font-weight: 700;
    color: #0f172a;
    flex-shrink: 0;
    text-align: right;
    min-width: 50px;
}

.dqd-metric-item.dqd-ratio-high .dqd-metric-number {
    color: #dc2626;
}

.dqd-metric-item.dqd-ratio-medium .dqd-metric-number {
    color: #ea580c;
}

.dqd-metric-item.dqd-ratio-low .dqd-metric-number {
    color: #64748b;
}

.dqd-metric-item.dqd-anomaly-exists .dqd-metric-number {
    color: #dc2626;
}

/* 스켈레톤 스타일 */
.dqd-skeleton {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: dqd-skeleton-loading 1.5s ease-in-out infinite;
    border-radius: 4px;
}

@keyframes dqd-skeleton-loading {
    0% {
        background-position: 200% 0;
    }

    100% {
        background-position: -200% 0;
    }
}

.dqd-skeleton-date {
    width: 140px;
    height: 20px;
}

.dqd-skeleton-status {
    width: 60px;
    height: 28px;
}

.dqd-skeleton-label {
    width: 100px;
    height: 16px;
}

.dqd-skeleton-value {
    width: 50px;
    height: 16px;
}

.dqd-skeleton-title {
    width: 120px;
    height: 18px;
}

.dqd-skeleton-count {
    width: 50px;
    height: 16px;
}

.dqd-skeleton-facility {
    width: 100px;
    height: 16px;
    margin-bottom: 6px;
}

.dqd-skeleton-addr {
    width: 150px;
    height: 12px;
}

.dqd-skeleton-badge {
    width: 40px;
    height: 20px;
    border-radius: 4px;
}

.dqd-skeleton-metric-name {
    width: 80px;
    height: 14px;
}

.dqd-skeleton-metric-number {
    width: 40px;
    height: 14px;
}
</style>